import { useState, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ClientProvider from "../modules/ClientProvider";
import { IScheduler, SchedulerType } from "../types";

interface UseSchedulerByTypeOptions {
    projectTag: string;
    type: SchedulerType;
    isPublic?: boolean;
    enabled?: boolean;
}

export const useSchedulerByType = ({ 
    projectTag, 
    type, 
    isPublic = false, 
    enabled = true 
}: UseSchedulerByTypeOptions) => {
    const [scheduler, setScheduler] = useState<IScheduler | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { getAccessTokenSilently } = useAuth0();

    const fetchScheduler = useCallback(async (): Promise<IScheduler | null> => {
        if (!projectTag || !enabled) return null;

        setLoading(true);
        setError(null);

        // Create client inside callback to avoid dependency issues
        const schedulerClient = ClientProvider.provideSchedulerClient();

        try {
            let response;

            if (isPublic) {
                response = await schedulerClient.getPublicSchedulerByType(projectTag, type);
            } else {
                const accessToken = await getAccessTokenSilently();
                response = await schedulerClient.getSchedulerByType(projectTag, type, accessToken);
            }

            // Check if response exists and has data
            if (!response || !response.data) {
                console.error(`No data returned for ${type} scheduler`);
                setError(`No data returned for ${type} scheduler`);
                return null;
            }

            const { data, error } = response;

            if (error) {
                console.error("Error fetching scheduler:", error);
                setError(error.message || "Failed to fetch scheduler");
                return null;
            } else {
                const fetchedScheduler = data.scheduler;
                // Only return scheduler if it has a valid scheduling URL
                if (fetchedScheduler?.scheduling_url && fetchedScheduler.scheduling_url !== "") {
                    setScheduler(fetchedScheduler);
                    return fetchedScheduler;
                } else {
                    setScheduler(null);
                    return null;
                }
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
            console.error("Error fetching scheduler:", err);
            setError(errorMessage);
            return null;
        } finally {
            setLoading(false);
        }
    }, [projectTag, type, isPublic, enabled]);

    useEffect(() => {
        fetchScheduler();
    }, [fetchScheduler]);

    return {
        scheduler,
        loading,
        error,
        refetch: fetchScheduler
    };
}; 